import { tmdbImage } from "./tmdb";

const TMDB_KEY = process.env.REACT_APP_TMDB_API_KEY;

async function tmdbFetch(path, params = {}, pages = 5) {
  if (!TMDB_KEY) return [];

  const allResults = [];

  for (let page = 1; page <= pages; page++) {
    const search = new URLSearchParams({
      api_key: TMDB_KEY,
      language: "en-US",
      include_adult: "false",
      page: String(page),
      ...params,
    });

    const res = await fetch(`https://api.themoviedb.org/3${path}?${search}`);
    if (!res.ok) continue;

    const json = await res.json();
    allResults.push(...(json.results || []));
  }

  return allResults;
}

function normalizeItem(item, fallbackType) {
  return {
    id: item.id,
    mediaType: item.media_type || fallbackType,
    title: item.title || item.name,
    year: (item.release_date || item.first_air_date || "").slice(0, 4),
    overview: item.overview,
    rating: item.vote_average,
    poster: tmdbImage(item.poster_path, "w342"),
    backdrop: tmdbImage(item.backdrop_path, "w780"),
  };
}

function seededShuffle(items, seedText = "") {
  const arr = [...items];

  let seed = 0;
  for (let i = 0; i < seedText.length; i++) {
    seed = (seed * 31 + seedText.charCodeAt(i)) >>> 0;
  }

  function random() {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  }

  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

function rotatePlaylists(playlists, count = 40) {
  const seed = `${Date.now()}-${Math.random()}`;
  return seededShuffle(playlists, `homepage-${seed}`).slice(0, count);
}

export const TMDB_PLAYLISTS = {
  home: [
    {
      id: "the-hit-list",
      title: "The Hit List",
      type: "mixed",
      path: "/trending/all/week",
      pages: 5,
      params: {},
    },
    {
      id: "critically-acclaimed",
      title: "Critically Acclaimed",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "vote_average.desc",
        "vote_count.gte": "1000",
        "vote_average.gte": "7.5",
      },
    },
    {
      id: "blockbuster-movies",
      title: "Blockbuster Movies",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "revenue.desc",
        "vote_count.gte": "1000",
      },
    },
    {
      id: "90s-nostalgia",
      title: "'90s Nostalgia",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        "primary_release_date.gte": "1990-01-01",
        "primary_release_date.lte": "1999-12-31",
        "vote_count.gte": "100",
      },
    },
    {
      id: "80s-throwbacks",
      title: "'80s Throwbacks",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        "primary_release_date.gte": "1980-01-01",
        "primary_release_date.lte": "1989-12-31",
        "vote_count.gte": "100",
      },
    },
    {
      id: "movies-about-friendship",
      title: "Movies about Friendship",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_keywords: "6054",
        "vote_count.gte": "50",
      },
    },
    {
      id: "movies-based-on-books",
      title: "Movies Based on Books",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_keywords: "818",
        "vote_count.gte": "50",
      },
    },
    {
      id: "non-stop-action",
      title: "Non-Stop Action",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "28",
        "vote_count.gte": "100",
      },
    },
    {
      id: "haunting-horror",
      title: "Haunting Horror",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "27",
        "vote_count.gte": "50",
      },
    },
    {
      id: "need-a-laugh",
      title: "Need a Laugh?",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "35",
        "vote_count.gte": "100",
      },
    },
    {
      id: "here-for-the-drama",
      title: "Here for The Drama",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "18",
        "vote_count.gte": "100",
      },
    },
    {
      id: "twists-and-turns",
      title: "Twists & Turns",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "53,9648",
        "vote_count.gte": "100",
      },
    },
    {
      id: "cult-classics",
      title: "Cult Classics",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_keywords: "703",
        "vote_count.gte": "50",
      },
    },
    {
      id: "heist-films",
      title: "Heist Films",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_keywords: "10051",
        "vote_count.gte": "50",
      },
    },
    {
      id: "feel-good-flicks",
      title: "Feel-Good Flicks",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "35,10751,10749",
        "vote_count.gte": "75",
      },
    },
    {
      id: "family-drama",
      title: "Family Drama",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "18",
        with_keywords: "18035",
        "vote_count.gte": "50",
      },
    },
    {
      id: "fun-for-everyone",
      title: "Fun for Everyone",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "10751,16,35,12",
        "vote_count.gte": "100",
      },
    },
    {
      id: "biopics",
      title: "Biopics",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_keywords: "5565",
        "vote_count.gte": "50",
      },
    },
    {
      id: "stoner-movies",
      title: "Stoner Movies",
      type: "movie",
      path: "/discover/movie",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_keywords: "171841",
        "vote_count.gte": "25",
      },
    },
    {
      id: "bingeworthy-tv",
      title: "Bingeworthy TV Shows",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        "vote_average.gte": "7",
        "vote_count.gte": "100",
      },
    },
    {
      id: "late-90s-sitcoms",
      title: "Late 90s Sitcoms",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "35",
        "first_air_date.gte": "1995-01-01",
        "first_air_date.lte": "2002-12-31",
      },
    },
    {
      id: "comfort-tv",
      title: "Comfort TV",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "35,10751",
        "vote_count.gte": "50",
      },
    },
    {
      id: "hbo-original-series",
      title: "HBO Original Series",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_networks: "49",
      },
    },
    {
      id: "true-crime-spree",
      title: "True Crime Spree",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        with_genres: "80,99",
        "vote_count.gte": "25",
      },
    },
    {
      id: "essential-docuseries",
      title: "Essential Docuseries",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "vote_average.desc",
        with_genres: "99",
        "vote_count.gte": "50",
      },
    },
    {
      id: "retro-tv",
      title: "Retro TV",
      type: "tv",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        "first_air_date.lte": "1995-12-31",
        "vote_count.gte": "50",
      },
    },
    {
      id: "growing-up-millennial",
      title: "Growing Up Millennial",
      type: "mixed",
      path: "/discover/tv",
      pages: 6,
      params: {
        sort_by: "popularity.desc",
        "first_air_date.gte": "1990-01-01",
        "first_air_date.lte": "2010-12-31",
        with_genres: "35,10751,16",
      },
    },
  ],
};

export async function getTMDBPlaylist(playlist) {
  const results = await tmdbFetch(
    playlist.path,
    playlist.params,
    playlist.pages || 5
  );

  return results.map((item) => normalizeItem(item, playlist.type));
}

export async function getTMDBPlaylists(section = "home") {
  const playlists = TMDB_PLAYLISTS[section] || TMDB_PLAYLISTS.home;
  const selectedPlaylists =
    section === "home" ? rotatePlaylists(playlists, 40) : playlists;

  return Promise.all(
    selectedPlaylists.map(async (playlist) => ({
      ...playlist,
      items: await getTMDBPlaylist(playlist),
    }))
  );
}
